import React, {
	memo,
	useCallback,
	useEffect,
	useState,
} from 'react';

import { FullChartCard, Modal } from '@components/common';

import { useKPI } from '@hooks/kpis/useKPI';

import { kpi } from '@store/selectors';
import { useAppDispatch, useAppSelector } from '@store/store';

import { setCompareCharts, setCorrelateCharts } from '@slices/kpi';

import { getListOfKpis, IKpiListInterface } from '@utils/kpi-utils/getKpisListOnMap';

import { IChart } from '@interfaces/kpi/kpiCommon';

import styles from './KpiSelectModalOnMap.module.scss';

interface IKpiSelectModalOnMapProps {
	charts: IChart[];
}

const KpiSelectModalOnMap = ({ charts }: IKpiSelectModalOnMapProps) => {
	const dispatch = useAppDispatch();

	const {
		compareCharts,
		compareSettings,
		correlateCharts,
		correlateSettings,
	} = useAppSelector(kpi);

	const {
		drillDownCharts,
		exportCharts,
		getDrillDownCharts,
		isLoadingDrillDownCharts,
		optionsLimenetKpi,
		optionsNokiaKpi,
	} = useKPI();

	const [kpisList, setKpisList] = useState<IKpiListInterface>({});

	const [fullChart, setFullChart] = useState<IChart | null>(null);

	useEffect(() => {
		if (charts.length) {
			const list = getListOfKpis(charts, optionsLimenetKpi, optionsNokiaKpi);

			setKpisList(list);
		}
	}, [charts, optionsLimenetKpi, optionsNokiaKpi]);

	const onKpiSelect = useCallback((kpiChart: IChart) => () => {
		setFullChart(kpiChart);
	}, []);

	const closeFullCard = useCallback(() => {
		setFullChart(null);
	}, []);

	const addChartToCompare = (chart: IChart) => dispatch(setCompareCharts({ chart }));

	const addChartToCorrelate = (chart: IChart) => dispatch(setCorrelateCharts({ chart }));

	return (
		<div className={styles.listWrapper}>
			{Object.entries(kpisList).map(([category, kpis]) => {
				return (
					<div
						key={category}
						className={styles.categoryWrapper}
					>
						<div className={styles.categoryName}>{category}</div>
						<div className={styles.kpisList}>
							{kpis.map(kpi => {
								return (
									<div
										key={kpi.id}
										className={styles.kpiItem}
										onClick={onKpiSelect(kpi.chart)}
									>
										{kpi.title}
									</div>
								);
							})}
						</div>
					</div>
				);
			})}
			{fullChart && (
				<Modal
					isOpen={!!fullChart}
					onClose={closeFullCard}
					title={fullChart.name}
					width='80%'
				>
					<FullChartCard
						addChartToCompare={addChartToCompare}
						addChartToCorrelate={addChartToCorrelate}
						chart={fullChart}
						compareCharts={compareCharts}
						compareSettings={compareSettings}
						correlateCharts={correlateCharts}
						correlateSettings={correlateSettings}
						drillDownCharts={drillDownCharts}
						exportCharts={exportCharts}
						getDrillDownCharts={getDrillDownCharts}
						isLoadingDrillDownCharts={isLoadingDrillDownCharts}
					/>
				</Modal>
			)}
		</div>
	);
};

export default memo(KpiSelectModalOnMap);
